import React from 'react';
import { Link } from 'react-router-dom';
import { URL_SPLIT_REGEX } from '@/lib/urls';

interface LinkifiedTextProps {
  text: string;
}

// Regex to match mentions: @everyone or @[user_id:display_name]
const MENTION_REGEX = /@(everyone|\[([a-f0-9-]+):([^\]]+)\])/g;

function renderTextWithMentions(text: string, keyPrefix: string): React.ReactNode[] {
  const result: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  // Reset regex state
  MENTION_REGEX.lastIndex = 0;

  while ((match = MENTION_REGEX.exec(text)) !== null) {
    // Add text before this match
    if (match.index > lastIndex) {
      result.push(text.slice(lastIndex, match.index));
    }

    if (match[1] === 'everyone') {
      // @everyone mention
      result.push(
        <span
          key={`${keyPrefix}-mention-${match.index}`}
          className="inline-flex items-center bg-primary/10 text-primary font-medium px-1.5 py-0.5 rounded text-sm"
        >
          @everyone
        </span>
      );
    } else {
      // User mention: @[user_id:display_name]
      const userId = match[2];
      const displayName = match[3];
      result.push(
        <Link
          key={`${keyPrefix}-mention-${match.index}`}
          to={`/members/${userId}`}
          className="inline-flex items-center bg-primary/10 text-primary font-medium px-1.5 py-0.5 rounded text-sm hover:bg-primary/20 transition-colors"
        >
          @{displayName}
        </Link>
      );
    }

    lastIndex = MENTION_REGEX.lastIndex;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex));
  }

  return result.length > 0 ? result : [text];
}

export function LinkifiedText({ text }: LinkifiedTextProps) {
  const parts = text.split(URL_SPLIT_REGEX);

  return (
    <>
      {parts.map((part, i) => {
        // Every odd index is a captured URL (from the capturing group in split)
        if (URL_SPLIT_REGEX.test(part)) {
          // Reset lastIndex since we're using a global regex
          URL_SPLIT_REGEX.lastIndex = 0;
          const cleanedUrl = part.replace(/[.,;:!?)}\]]+$/, '');
          const trailing = part.slice(cleanedUrl.length);
          return (
            <React.Fragment key={i}>
              <a
                href={cleanedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline break-all"
              >
                {cleanedUrl}
              </a>
              {trailing}
            </React.Fragment>
          );
        }
        // Render text with mentions
        return (
          <React.Fragment key={i}>
            {renderTextWithMentions(part, `part-${i}`)}
          </React.Fragment>
        );
      })}
    </>
  );
}
